import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2, Loader2, Music4, Sparkles, Star } from "lucide-react";
import api, { publicMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function CommunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/community/${id}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: item?.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }
    } catch { /* cancelled */ }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grain flex flex-col items-center justify-center px-6 text-center" data-testid="community-detail-notfound">
        <div className="mb-8"><Logo /></div>
        <h1 className="font-head text-2xl font-bold mb-2">Publicación no disponible</h1>
        <p className="text-sm text-[#94A3B8] mb-6">Puede que el creador la haya quitado de la galería.</p>
        <button onClick={() => navigate("/comunidad")} className="px-5 py-2.5 rounded-full bg-[#FF334B] text-white font-semibold">Volver a la Comunidad</button>
      </div>
    );
  }
  if (!item) return <div className="min-h-screen grain flex items-center justify-center"><Loader2 className="w-7 h-7 text-[#FF334B] spin" /></div>;

  const r = item.result || {};
  const videoPath = r.export_path || r.concat_path || r.video_path || (item.scenes?.[0]?.video_path);
  const audioPath = r.audio_path;
  const poster = r.poster_path ? publicMediaUrl(r.poster_path) : null;

  return (
    <div className="min-h-screen grain relative overflow-hidden pb-20" data-testid="community-detail-page">
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-6">
        <Logo />
        <button onClick={() => navigate("/comunidad")} className="text-sm font-semibold px-4 py-2 rounded-full border border-white/15 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Comunidad
        </button>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-5 sm:px-10">
        <div className="mfp-card p-4 sm:p-6">
          <div className="rounded-xl overflow-hidden bg-black mb-5">
            {item.is_audio || (!videoPath && audioPath) ? (
              <div className="relative">
                {poster && <img src={poster} alt={item.title} className="w-full aspect-square object-cover" />}
                {audioPath && <audio data-testid="community-audio" controls className="w-full mt-2" src={publicMediaUrl(audioPath)} />}
              </div>
            ) : videoPath ? (
              <video data-testid="community-video" controls playsInline poster={poster || undefined} className="w-full aspect-video bg-black" src={publicMediaUrl(videoPath)} />
            ) : (
              <div className="aspect-video flex items-center justify-center text-white/40"><Music4 className="w-10 h-10" /></div>
            )}
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-head text-2xl font-bold truncate">{item.title}</h1>
                {item.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FF9F2E] text-black"><Star className="w-3 h-3" />Destacado</span>}
              </div>
              <p className="text-sm text-[#94A3B8] mt-1">por <span className="text-white font-medium">{item.creator_name}</span> · {item.category}</p>
            </div>
            <button data-testid="community-share-btn" onClick={share} className="shrink-0 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 transition-colors">
              <Share2 className="w-4 h-4" /> Compartir
            </button>
          </div>

          {!user && (
            <button data-testid="community-detail-cta" onClick={() => navigate("/")}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors">
              <Sparkles className="w-4 h-4" /> Crea el tuyo con Metáfora Play
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
